import { simplify } from '@turf/turf';
import { generateKeyBetween } from 'fractional-indexing';
import type { LineString, Point, Polygon } from 'geojson';
import { nanoid } from 'nanoid';
import type { FeatureArray, LineObject, MapObject, MarkerObject, PolygonObject } from '~/common/types';
import store from '~/pages/projectDetails/civildraw/store';

export default async function renderObjects(drawingObjects: FeatureArray) {
  const newObjects: MapObject[] = [];

  for (let i = 0; i < drawingObjects.length; i++) {
    const { geometry, properties } = drawingObjects[i];
    let color: string = '#880808';
    if (properties?.stroke) {
      color = properties.stroke;
    }

    const sortOrder = generateSortOrderKey();

    let description = '';

    if (properties?.description) {
      if (typeof properties.description !== 'string') {
        console.error(
          `Error: Invalid description on ${geometry?.type} ${properties.name || 'Unnamed Object'}.
          The description cannot be of type "${properties.description['@type'] || 'Unknown Type'}".
          The description must be valid string text.
          `,
        );
      } else {
        description = properties.description;
      }
    }

    if (geometry?.type === 'LineString' || geometry?.type === 'Point' || geometry?.type === 'Polygon') {
      const { type } = geometry;

      let obj: MapObject;

      switch (type) {
        case 'LineString':
          obj = {
            id: nanoid(),
            sortOrder,
            type: 'line',
            title: properties?.name || 'New Line',
            description,
            geometry: simplify(
              {
                type,
                coordinates: (geometry as LineString).coordinates.map((point) => [point[0], point[1]]),
              },
              {
                tolerance: 0.0001,
                mutate: true,
              },
            ),
            properties: {
              color,
              strokeOpacity: properties?.['stroke-opacity'] || 1,
              strokeWidth: properties?.['stroke-width'] || 4,
              strokeStyle: 'solid',
              showLength: false,
              showTitle: false,
            },
          } as LineObject;
          break;

        case 'Point':
          obj = {
            id: nanoid(),
            sortOrder,
            type: 'marker',
            title: properties?.name || 'New Marker',
            description,
            geometry: {
              type,
              coordinates: [(geometry as Point).coordinates[0], (geometry as Point).coordinates[1]],
            },
            properties: {
              color,
            },
          } as MarkerObject;
          break;

        case 'Polygon':
          obj = {
            id: nanoid(),
            sortOrder,
            type: 'polygon',
            title: properties?.name || 'New Polygon',
            description,
            geometry: simplify(
              {
                type,
                coordinates: [(geometry as Polygon).coordinates[0].map((point) => [point[0], point[1]])],
              },
              { tolerance: 0.0001, mutate: true },
            ),
            properties: {
              color,
              fillOpacity: properties?.['fill-opacity'] | 0.25,
              strokeOpacity: properties?.['stroke-opacity'] || 1,
              strokeWidth: properties?.['stroke-width'] || 4,
              strokeStyle: 'solid',
              showArea: false,
              showTitle: false,
            },
          } as PolygonObject;
          break;
      }

      newObjects.push(obj);
    }
  }

  await store.addManyObjects(newObjects);
}

function generateSortOrderKey() {
  const objects = store.objectsOnMainSurface;
  const keys = Object.keys(objects);
  if (keys.length < 1) {
    return 'a1';
  } else {
    const lastSortOrder = objects[keys[keys.length - 1]].sortOrder;
    return generateKeyBetween(lastSortOrder, null);
  }
}
